/**
 * Supabase клиент для работы с авторизацией
 */

import { createClient, SupabaseClient, User, Session } from '@supabase/supabase-js';
import config from './config';

let supabase: SupabaseClient | null = null;

// Создаем клиент только если авторизация включена и настроена
if (config.authEnabled && config.supabaseUrl && config.supabaseAnonKey) {
  supabase = createClient(config.supabaseUrl, config.supabaseAnonKey);
}

export { supabase };

// Типы для авторизации
export interface AuthUser {
  id: string;
  email?: string;
  user_metadata?: Record<string, any>;
  app_metadata?: Record<string, any>;
}

export interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
}

// Утилитарные функции
export const authUtils = {
  /**
   * Проверяем, доступна ли авторизация
   */
  isAuthAvailable(): boolean {
    return config.authEnabled && supabase !== null;
  },

  /**
   * Получаем текущую сессию
   */
  async getCurrentSession(): Promise<Session | null> {
    if (!supabase) return null;
    
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  /**
   * Получаем токен для API запросов
   */
  async getAccessToken(): Promise<string | null> {
    // Сначала проверяем Telegram токен из localStorage
    const telegramToken = localStorage.getItem('telegram_token');
    if (telegramToken) {
      return telegramToken;
    }
    
    // Если нет Telegram токена, используем Supabase токен
    if (!supabase) return null;
    
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  },

  /**
   * Авторизация через Google
   */
  async signInWithGoogle(): Promise<{ error: any }> {
    if (!supabase) {
      return { error: new Error('Supabase not configured') };
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    return { error };
  },

  /**
   * Авторизация через magic link (email)
   */
  async signInWithEmail(email: string, guest_id?: string | null, guest_game_id?: string | null): Promise<{ error: any }> {
    if (!supabase) {
      return { error: new Error('Supabase not configured') };
    }

    // Формируем redirect URL с guest параметрами если они есть
    let redirectUrl = `${window.location.origin}/auth/callback`;
    if (guest_id && guest_game_id) {
      const params = new URLSearchParams({
        guest_id,
        guest_game_id
      });
      redirectUrl += `?${params.toString()}`;
      console.log('📧 Email Magic Link will redirect to:', redirectUrl);
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl
      }
    });

    return { error };
  },

  /**
   * Выход из системы
   */
  async signOut(): Promise<{ error: any }> {
    if (!supabase) {
      return { error: new Error('Supabase not configured') };
    }

    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      // Логируем ошибку, но не блокируем выход
      console.warn('Supabase signOut failed:', error);
      return { error };
    }
  },

  /**
   * Подписка на изменения авторизации
   */
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    if (!supabase) return { data: { subscription: null } };
    
    return supabase.auth.onAuthStateChange(callback);
  }
};

export default supabase;
